/**
 * useDayEvents Hook
 * React Query hook for fetching all events for a specific day
 * Used by the DayEventsPopup component
 */

import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { fetchCalendarEvents } from '../api/calendarApi';
import { transformDayEvents } from '../utils/eventFormatters';
import { queryKeys } from 'hooks/config/queryClient';

/**
 * Fetch events for a specific day
 *
 * @param {object} options - Query options
 * @param {string} options.date - Date to fetch events for (YYYY-MM-DD or Date object)
 * @param {string} options.timezone - User timezone
 * @param {array} options.selectedUsers - Selected user IDs
 * @param {object} options.filters - Applied filters
 * @param {string} options.case_id - Case ID for filtering
 * @param {boolean} options.enabled - Enable/disable query
 * @returns {object} React Query result
 */
export const useDayEvents = ({
  date,
  timezone = 'America/Chicago',
  selectedUsers = [],
  filters = {},
  case_id = null,
  contact_id = null,
  enabled = true,
}) => {
  // Create date range for the specific day
  const dayStart = date ? moment.tz(date, timezone).startOf('day').format() : null;
  const dayEnd = date ? moment.tz(date, timezone).add(1, 'day').endOf('day').format() : null;

  // Stable keys for React Query
  const dateKey = dayStart;
  const selectedUsersKey = Array.isArray(selectedUsers) ? selectedUsers.join('|') : '';
  const filtersKey = filters ? JSON.stringify(filters) : '';

  const queryKey = queryKeys.calendarEvents.dayEvents(dateKey, filtersKey, selectedUsersKey, timezone, case_id, contact_id);

  const isEnabled = Boolean(enabled && date);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await fetchCalendarEvents(
        dayStart,
        dayEnd,
        'timeGridDay', // Use day view to get all events
        selectedUsers,
        timezone,
        case_id,
        contact_id,
        filters
      );

      return transformDayEvents(result?.data, timezone, dayStart, dayEnd);
    },
    enabled: isEnabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    events: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export default useDayEvents;
